import { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * ScrollToTop — global scroll restoration.
 *
 * Resets the window scroll to (0, 0) on every route change so the user always
 * lands at the top of a newly-navigated page. If the destination URL includes
 * a hash (e.g. `/#the-studio`), scroll to that element instead so in-page
 * anchor links continue to work.
 *
 * A follow-up scroll on the next animation frame catches any small layout
 * shifts (e.g. late-arriving hero images) that would otherwise leave the page
 * a few pixels off the true top.
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useLayoutEffect(() => {
    const scrollToHash = () => {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ block: "start" });
        return true;
      }
      return false;
    };

    const scrollTop = () => window.scrollTo(0, 0);

    if (hash) {
      if (scrollToHash()) return;
    }
    scrollTop();
    // Re-assert on the next frame to defeat layout shifts from lazy assets.
    requestAnimationFrame(() => {
      if (hash && scrollToHash()) return;
      scrollTop();
    });
  }, [pathname, hash]);

  return null;
}
